import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import useCheckoutSteps from "../checkout/useCheckoutSteps";
import useTrackerContext from "../useTrackerContext";
import useRouteName from "../useRouteName";
import useGlobalContext from "../useGlobalContext";

import { pricingUtils } from "@flights/web-api-utils-universal";

import { getFlight } from "../../store/flightDetails/selectors";
import { getSearchId } from "../../store/searchResults/selectors";
import { getOrder } from "../../store/order/selectors";
import { RouteName } from "@flights/constants";

type ProductType = "flights";
type ProcessName = "travel_product_booking_process";
type ProcessType = "ticket" | "pax" | "extras" | "seats" | "fare" | "sirf" | "payment";

type ActionName<P extends ProcessType> = `${ProductType}__${ProcessName}_${P}`;

const actionVersions = {
  ticket: "1.0.0",
  pax: "1.0.0",
  extras: "1.0.0",
  seats: "1.0.0",
  fare: "1.0.0",
  sirf: "1.0.0",
  payment: "1.0.0"
};

type CheckoutRoutes = Extract<
  RouteName,
  | "checkout-ticket-type"
  | "checkout-pax"
  | "checkout-extras"
  | "checkout-seat-selection"
  | "checkout-fare"
  | "checkout-sirf"
  | "checkout-payment"
>;

const checkoutRoutesToProcessType: Record<CheckoutRoutes, ProcessType> = {
  "checkout-ticket-type": "ticket",
  "checkout-pax": "pax",
  "checkout-extras": "extras",
  "checkout-seat-selection": "seats",
  "checkout-fare": "fare",
  "checkout-sirf": "sirf",
  "checkout-payment": "payment"
};

type FlightBookingProcessContent = {
  search_id: string;
  correlation_id: string;
  booking_process_name: string;
  booking_process_number: number;
  booking_process_id: string;
  vertical_session_id: string;
  product_selected: {
    product_id: string;
    product_item_type: string;
    booking_vertical: "Flights";
    cancellation_policy: string;
    free_cancellation_until_date: string;
    cancellation_cost: number;
    cancellation_cost_currency: string;
    review_rating_score: number;
    review_count: number;
    displayed_price: {
      original_price: number;
      discounted_price: number;
      discount_breakdown: number[];
      discounted_type: string[];
      currency: string;
      displayed_original_price_eur: number;
      displayed_discounted_price_eur: number;
      discount_breakdown_eur: number;
    };
    supplier_price: {
      original_price: number;
      discounted_price: number;
      discount_breakdown: number[];
      currency: string;
    };
  };
  start_date: string;
  end_date: string;
  item_quantity: number;
  item_quantity_unit: string;
  has_special_requests: boolean;
  promo_code_applied: string;
  cross_sell?: Record<string, unknown>;
  marketing_consent?: Record<string, unknown>;
  payment_later_due_date: string;
  payment_goes_to: string;
  booking_status: {
    is_confirmed: boolean;
    reservation_number: string;
    status_update_time: string;
  };
};

const useSendTravelProductBookingProcessC360Event = () => {
  const { availableCheckoutSteps } = useCheckoutSteps();
  const routeName = useRouteName();
  const { trackExternal } = useTrackerContext();
  const { currencyCode, locale, analyticSessionId } = useGlobalContext();

  const flight = useSelector(getFlight);
  const searchId = useSelector(getSearchId);
  const order = useSelector(getOrder);

  const data = useMemo(() => {
    const departure = flight?.segments?.[0]?.departureTime;
    const cabinClass = flight?.segments?.[0]?.legs?.[0]?.cabinClass;
    const arrival = flight?.tripType === "ROUNDTRIP" ? flight?.segments.at(-1)?.arrivalTime : "";
    const startTime = departure ? format(new Date(departure), "dd MM yyyy, HH:mm") : "";
    const endTime = arrival ? format(new Date(arrival), "dd MM yyyy, HH:mm") : "";

    return {
      flight,
      searchId,
      analyticSessionId,
      currencyCode,
      locale,
      startTime,
      endTime,
      order,
      cabinClass
    };
  }, [currencyCode, flight, locale, order, searchId, analyticSessionId]);

  return useCallback(() => {
    const currentCheckoutStep = availableCheckoutSteps.find((step) => step.name === routeName);

    if (currentCheckoutStep) {
      const stepNumber = currentCheckoutStep.position - 1; // position 1 is checkout-start
      const stepName = getStepName(routeName);

      if (!stepName) {
        return;
      }

      trackExternal({
        type: "c360",
        args: [
          {
            action_name: getActionName(stepName),
            action_version: getActionVersion(stepName),
            content: getContent(stepNumber, stepName, data)
          }
        ]
      });
    }
  }, [availableCheckoutSteps, routeName, trackExternal, data]);
};

const getActionName = (stepName: ProcessType): ActionName<ProcessType> =>
  `flights__travel_product_booking_process_${stepName}`;
const getActionVersion = (stepName: string) => actionVersions[stepName as keyof typeof actionVersions] || "1.0.0";

const isCheckoutRoute = (routeName: RouteName): routeName is CheckoutRoutes => {
  return Object.keys(checkoutRoutesToProcessType).includes(routeName);
};

const getStepName = (routeName: RouteName): ProcessType | "" => {
  if (isCheckoutRoute(routeName)) {
    return checkoutRoutesToProcessType[routeName];
  }
  return "";
};

const getContent = (stepNumber: number, stepName: string, data: any): FlightBookingProcessContent => {
  const { flight } = data;

  const getOriginalPrice = () => {
    if (!!data.order) {
      return pricingUtils.priceToValue(data.order.totalPrice.totalWithoutDiscount);
    }
    return pricingUtils.priceToValue(flight.priceBreakdown.totalWithoutDiscount);
  };

  const getDiscountedPrice = () => {
    if (!!data.order) {
      return pricingUtils.priceToValue(data.order.totalPrice.total);
    }
    return pricingUtils.priceToValue(flight.priceBreakdown.total);
  };

  const getSupplierPrice = () => {
    if (!!data.order) {
      return pricingUtils.priceToValue(data.order.totalPrice.baseFare);
    }
    return pricingUtils.priceToValue(flight.priceBreakdown.baseFare);
  };

  const currency = data.order?.totalPrice?.baseFare?.currency || flight.priceBreakdown?.baseFare?.currency;

  return {
    search_id: data.searchId || "",
    correlation_id: "",
    booking_process_name: stepName || "",
    booking_process_number: stepNumber || 0,
    booking_process_id: flight.token || "",
    vertical_session_id: data.analyticSessionId,
    product_selected: {
      product_id: flight.offerReference,
      product_item_type: data.cabinClass || "",
      booking_vertical: "Flights",
      cancellation_policy: "",
      free_cancellation_until_date: "",
      cancellation_cost: 0,
      cancellation_cost_currency: "",
      review_rating_score: 0,
      review_count: 0,
      displayed_price: {
        original_price: getOriginalPrice(), // check price with cfar & ancillaries
        discounted_price: getDiscountedPrice(), // check price with cfar & ancillaries
        discount_breakdown: [0],
        discounted_type: [""],
        currency,
        displayed_original_price_eur: 0,
        displayed_discounted_price_eur: 0,
        discount_breakdown_eur: 0
      },
      supplier_price: {
        original_price: getSupplierPrice(),
        discounted_price: 0,
        discount_breakdown: [0],
        currency
      }
    },
    start_date: data.startTime,
    end_date: data.endTime,
    item_quantity: flight.travellers.length,
    item_quantity_unit: flight.tripType,
    has_special_requests: false,
    promo_code_applied: "",
    payment_later_due_date: "",
    payment_goes_to: "",
    booking_status: {
      is_confirmed: false,
      reservation_number: "",
      status_update_time: ""
    }
  };
};

export default useSendTravelProductBookingProcessC360Event;
